import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

function formatCompactNumber(value) {
    if (typeof value !== 'number') return value;
    if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(0) + 'k';
    return String(value);
}

export default function LineChart({ rows = [], xKey, yAxisKeys = [], theme = {}, onExposeLegend, onDataPointClick }) {
    const maxLen = Number(theme?.categoryMaxLen) > 0 ? Number(theme.categoryMaxLen) : 26;
    const categories = useMemo(() => {
        const names = Array.isArray(rows) ? rows.map(r => r?.[xKey]) : [];
        return names.map(n => typeof n === 'string' ? (n.length > maxLen ? n.slice(0, maxLen - 1) + '…' : n) : n);
    }, [rows, xKey, maxLen]);

    const series = useMemo(() => {
        const keys = Array.isArray(yAxisKeys) && yAxisKeys.length > 0 ? yAxisKeys : [];
        const isAreaSeries = theme?.variant === 'area' || theme?.area === true || theme?.type === 'area';
        return keys.map(key => ({
            name: String(key),
            type: isAreaSeries ? 'area' : 'line',
            data: Array.isArray(rows) ? rows.map(r => Number(r?.[key]) || 0) : []
        }));
    }, [rows, yAxisKeys, theme?.variant, theme?.area, theme?.type]);

    const colors = theme.seriesColors || ['#4F46E5', '#F59E0B', '#10B981', '#EF4444', '#6366F1', '#06B6D4'];
    const asArea = theme.variant === 'area' || theme.area === true || theme.type === 'area';
    const stacked = Boolean(theme.stacked && asArea);
    const showMarkers = theme?.showMarkers === false ? false : true; // default on
    const markerSize = Number(theme?.markerSize) >= 0 ? Number(theme.markerSize) : 3;
    const curve = typeof theme?.curve === 'string' ? theme.curve : (theme?.curved === false ? 'straight' : 'smooth');
    const useGradient = Boolean(asArea && (theme?.areaGradient === true || theme?.gradient === 'area'));

    const options = useMemo(() => {
        const labelColor = '#9ca3af';
        const xLabelColors = Array.isArray(categories) && categories.length ? new Array(categories.length).fill(labelColor) : [labelColor];
        const yLabelColors = ['#9ca3af'];
        const legendLabelColors = Array.isArray(series) && series.length ? new Array(series.length).fill('#64748b') : ['#64748b'];
        const showGridX = Boolean(theme?.showGridX);
        const showGridY = Boolean(theme?.showGridY);
        const showXAxisLabels = theme?.showXAxisLabels !== false; // default true
        const showYAxisLabels = theme?.showYAxisLabels !== false; // default true
        const showXAxisLine = Boolean(theme?.showXAxisLine);
        const showYAxisLine = Boolean(theme?.showYAxisLine);
        // legend options (theme.legend can be boolean or { show, position, horizontalAlign })
        const legendConfig = theme?.legend;
        const legendShow = legendConfig === false ? false : (legendConfig === true ? true : (typeof legendConfig?.show === 'boolean' ? legendConfig.show : true));
        const legendPosition = typeof legendConfig?.position === 'string' ? legendConfig.position : 'top';
        const legendAlign = typeof legendConfig?.horizontalAlign === 'string' ? legendConfig.horizontalAlign : 'left';
        return ({
        chart: {
            type: asArea ? 'area' : 'line',
            stacked,
            parentHeightOffset: 0,
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        stroke: { curve: curve, width: 2 },
        markers: { size: showMarkers ? markerSize : 0, strokeWidth: 0 },
        legend: { show: legendShow, position: legendPosition, horizontalAlign: legendAlign, labels: { colors: legendLabelColors } },
        dataLabels: { enabled: false },
        tooltip: { enabled: true },
        colors,
        grid: { show: Boolean(showGridX || showGridY), xaxis: { lines: { show: showGridX } }, yaxis: { lines: { show: showGridY } }, padding: { top: 12, left: 0, right: 0, bottom: -8 } },
        states: { hover: { filter: { type: 'none' } }, active: { filter: { type: 'none' } } },
        fill: asArea
            ? (useGradient
                ? {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: typeof theme?.gradientShadeIntensity === 'number' ? theme.gradientShadeIntensity : 0.3,
                        opacityFrom: typeof theme?.gradientOpacityFrom === 'number' ? theme.gradientOpacityFrom : 0.4,
                        opacityTo: typeof theme?.gradientOpacityTo === 'number' ? theme.gradientOpacityTo : 0.1,
                        stops: Array.isArray(theme?.gradientStops) ? theme.gradientStops : [0, 90, 100],
                        gradientToColors: Array.isArray(theme?.gradientToColors) ? theme.gradientToColors : undefined,
                    }
                }
                : { type: 'solid', opacity: typeof theme?.areaOpacity === 'number' ? theme.areaOpacity : 0.35 })
            : { type: 'solid', opacity: 1 },
        xaxis: {
            categories,
            title: theme?.xAxisTitle ? { text: theme.xAxisTitle, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } } : {text: undefined},
            axisTicks: { show: false },
            axisBorder: { show: showXAxisLine },
            labels: { show: showXAxisLabels, rotate: -35, trim: false, hideOverlappingLabels: false, style: { colors: xLabelColors, fontSize: '12px' } }
        },
        yaxis: {
            title: theme?.yAxisTitle ? { text: theme.yAxisTitle, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } } : {text: undefined},
            axisTicks: { show: false },
            axisBorder: { show: showYAxisLine },
            labels: { show: showYAxisLabels, formatter: val => `${formatCompactNumber(val)}`, style: { colors: yLabelColors, fontSize: '12px' } }
        },
        events: {
            dataPointSelection: (_event, _chartContext, config) => {
                const sIdx = config?.seriesIndex;
                const dIdx = config?.dataPointIndex;
                if (typeof sIdx === 'number' && typeof dIdx === 'number' && typeof onDataPointClick === 'function') {
                    const lbl = categories?.[dIdx];
                    const yKey = Array.isArray(yAxisKeys) ? yAxisKeys[sIdx] : undefined;
                    const yVal = Array.isArray(series?.[sIdx]?.data) ? series[sIdx].data[dIdx] : undefined;
                    const payload = {
                        seriesIndex: sIdx,
                        index: dIdx,
                        label: lbl,
                        value: yVal,
                        xKey,
                        yKey,
                        [xKey]: lbl,
                        ...(yKey ? { [yKey]: yVal } : {}),
                        row: Array.isArray(rows) ? rows[dIdx] : undefined,
                        type: asArea ? 'area' : 'line',
                    };
                    try { onDataPointClick(payload); } catch (_e) {}
                }
            }
        }
    })}, [categories, series, colors, asArea, stacked, theme]);

    // Expose legend items (name-color pairs) for external consumers after render commit
    const exposedItems = useMemo(() => (series || []).map((s, i) => ({ label: s?.name, color: colors?.[i] })), [series, colors]);
    React.useEffect(() => {
        if (typeof onExposeLegend === 'function') {
            onExposeLegend(exposedItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(exposedItems)]);

    return (
        <Chart
            type={asArea ? 'area' : 'line'}
            height={300}
            series={series}
            options={options}
        />
    );
}


